import React from 'react';
import clsx from 'clsx';
import { useTranslation } from 'next-i18next';
import { useContextSelector } from 'use-context-selector';
import { ModalContext } from '@context/modal.context';
import { useAnalytics } from '@context/analytics/analytics.context';
import useUser from '@hooks/user/use-user';
import {
  accountSettingsDeleteCardEventPayload,
  accountSettingsSecurityToggleEventPayload,
} from '@tracking/account-settings/account-settings-tracking';
import AccountSettingsSectionHeader from '@components/account-settings/account-settings-section-header/account-settings-section-header';
import Icon from '@components/atoms/icon/icon';
import Switch from '@components/catalog/switch/switch';
import usePaymentCards from '@hooks/account-payments/use-payment-cards';
import useUpdateDeleteCardsPermission from '@hooks/account-payments/use-update-delete-cards-permission';
import DeletePaymentCardConfirmation from './delete-payment-card-confirmation/delete-payment-card-confirmation';
import styles from './account-payments.module.scss';

const isExpired = (expirationDate: string) => new Date(expirationDate) < new Date();

const getLastFourDigits = (number: string) => number.slice(-4);

const AccountPayments: React.FC = () => {
  const { t } = useTranslation('account-settings');
  const { user } = useUser();
  const { data: cards = [] } = usePaymentCards(user?.id);
  const { mutate: updateDeleteCardsPermission } = useUpdateDeleteCardsPermission(user?.id ?? '');
  const openModal = useContextSelector(ModalContext, (v) => v.openModal);
  const { sendEvent } = useAnalytics();
  const hasCards = cards.length > 0;

  if (!user) {
    return null;
  }

  const handleSecurityToggle = (checked: boolean) => {
    sendEvent({
      type: 'event',
      payload: { ...accountSettingsSecurityToggleEventPayload, property: checked ? 'on' : 'off' },
    });
    updateDeleteCardsPermission(checked);
  };

  const handleDeleteClick = (cardId: number, lastFourDigits: string) => {
    sendEvent({ type: 'event', payload: accountSettingsDeleteCardEventPayload });
    openModal(
      <DeletePaymentCardConfirmation
        cardId={cardId}
        lastFourDigits={lastFourDigits}
      />,
      {
        fullScreenOnMobile: false,
        styles: {
          content: {
            width: 'calc(100% - 40px)',
            maxWidth: '600px',
          },
        },
      },
    );
  };

  return (
    <div
      className={styles.accountPayments}
      data-cy="account-payments-section"
    >
      <div className={styles.accountPayments__header}>
        <AccountSettingsSectionHeader
          title={t('ACCOUNT_SETTINGS.MENU.PAYMENT_METHODS')}
          isEditable={false}
        />
      </div>

      {hasCards ? (
        <>
          <p className={styles.accountPayments__text}>{t('ACCOUNT_SETTINGS.PAYMENT_METHODS.CARDS')}</p>
          <ul className={styles.accountPayments__list}>
            {cards.map((card) => {
              const expired = isExpired(card.expirationDate);
              const lastFour = getLastFourDigits(card.number);
              return (
                <li
                  key={card.id}
                  className={styles.accountPayments__item}
                  data-cy={`account-payment-card-${card.id}`}
                >
                  <img
                    width="32"
                    height="20"
                    className={clsx(styles.accountPayments__item__card)}
                    alt={card.brandUnformat}
                    loading="lazy"
                    src={card.picto}
                  />
                  <span
                    className={clsx(
                      styles.accountPayments__item__number,
                      expired && styles['accountPayments__item__number--expired'],
                    )}
                    data-cy="account-payment-card-info"
                  >
                    **** {lastFour}
                  </span>
                  <div className={styles.accountPayments__item__actions}>
                    <span
                      className={clsx(
                        styles.accountPayments__item__expiry,
                        expired && styles['accountPayments__item__expiry--expired'],
                      )}
                    >
                      {expired
                        ? t('ACCOUNT_SETTINGS.PAYMENT_METHODS.EXPIRED')
                        : t('ACCOUNT_SETTINGS.PAYMENT_METHODS.EXPIRES_IN').replace(
                            '%@',
                            card.expirationDateStandardFormat,
                          )}
                    </span>
                    <button
                      type="button"
                      className={styles.accountPayments__item__delete}
                      aria-label={t('ACCOUNT_SETTINGS.PAYMENT_METHODS.DELETE_CTA')}
                      data-cy={`account-payment-card-delete-${card.id}`}
                      onClick={() => handleDeleteClick(card.id, lastFour)}
                    >
                      <Icon
                        name="delete-default"
                        ariaHidden
                      />
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
          <p className={styles.accountPayments__hint}>{t('ACCOUNT_SETTINGS.PAYMENT_METHODS.TEXT')}</p>
          <div className={styles.accountPayments__security}>
            <div className={styles.accountPayments__security__content}>
              <p
                className={styles.accountPayments__security__title}
                id="security-options-label"
              >
                {t('ACCOUNT_SETTINGS.PAYMENT_METHODS.SECURITY_OPTION_TITLE')}
              </p>
              <p className={styles.accountPayments__security__description}>
                {t('ACCOUNT_SETTINGS.PAYMENT_METHODS.SECURITY_OPTION_TEXT')}
              </p>
            </div>
            <div>
              <Switch
                checked={user?.hasDeleteCardsPermission ?? false}
                onChange={handleSecurityToggle}
                ariaLabelledby="security-options-label"
                dataCyElementLocator="account-payments-security-toggle"
              />
            </div>
          </div>
        </>
      ) : (
        <p
          className={styles.accountPayments__hint}
          data-cy="account-payments-no-card"
        >
          {t('ACCOUNT_SETTINGS.PAYMENT_METHODS.NO_CARD')}
        </p>
      )}
      <div className={styles.accountPayments__info}>
        <div className={styles.accountPayments__info__header}>
          <Icon
            name="lock-default"
            ariaHidden
            className={styles.accountPayments__info__icon}
          />
          <p className={styles.accountPayments__info__title}>
            {t('ACCOUNT_SETTINGS.PAYMENT_METHODS.REASSURANCE_TITLE')}
          </p>
        </div>
        <ul className={styles.accountPayments__info__list}>
          <li>{t('ACCOUNT_SETTINGS.PAYMENT_METHODS.REASSURANCE_CONTENT_1')}</li>
          <li>{t('ACCOUNT_SETTINGS.PAYMENT_METHODS.REASSURANCE_CONTENT_2')}</li>
          <li>{t('ACCOUNT_SETTINGS.PAYMENT_METHODS.REASSURANCE_CONTENT_3')}</li>
        </ul>
      </div>
    </div>
  );
};

export default AccountPayments;
